import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import ProtectedRoute from './components/ProtectedRoute';
import { AnnexureFillingNavProvider } from './contexts/AnnexureFillingNavContext';
import AdminRoute from './components/AdminRoute';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import { ImsPlatformGuard, VmsPlatformGuard } from './components/PlatformRouteGuard';
import VMSLayout from './components/VMSLayout';
import VmsAdminRoute from './components/VmsAdminRoute';
import VmsLegalOrAdminRoute from './components/VmsLegalOrAdminRoute';
import VmsAnnexureManagementRoute from './components/VmsAnnexureManagementRoute';
import VmsDpiaManagementRoute from './components/VmsDpiaManagementRoute';
import VmsBusinessOwnerRoute from './components/VmsBusinessOwnerRoute';
import {
  OngoingAnnexuresLegacyRedirect,
  OngoingAnnexuresSegmentLegacyRedirect,
  OngoingVendorReviewsLegacyTabRedirect,
} from './pages/vms/ongoingAnnexureRoutes';
import OnboardingVendorAnnexuresRedirect from './pages/vms/OnboardingVendorAnnexuresRedirect';
import { PageLoader } from './components/LoadingStates';
import SystemSelection from './pages/SystemSelection';
import BrandDocumentHead from './components/BrandDocumentHead';
import { getPostLoginPath } from './utils/vmsRole';

function isInvoiceAdmin(user: { invoice_permission?: string } | null): boolean {
  const p = user?.invoice_permission;
  return p === 'admin' || p === 'super_admin';
}

/** After login, `/` sends admins to the dashboard and everyone else to invoices. */
function DefaultAppRedirect() {
  const { user, loading } = useAuth();
  if (loading) {
    return <PageLoader message="Loading..." />;
  }
  return <Navigate to={isInvoiceAdmin(user) ? '/ims/dashboard' : '/ims/invoices'} replace />;
}

/** Unknown routes: IMS-only users go to IMS; VMS users may see system selection. */
function AuthenticatedCatchAllRedirect() {
  const { user, loading } = useAuth();
  if (loading) {
    return <PageLoader message="Loading..." />;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return <Navigate to={getPostLoginPath(user)} replace />;
}

function RedirectLegacyOutsourcingVendorDetail() {
  const { id } = useParams<{ id: string }>();
  const target = id ? `/vms/admin-config/register/${encodeURIComponent(id)}` : '/vms/admin-config/register';
  return <Navigate to={target} replace />;
}

// Lazy load all page components for code splitting
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));

// IMS pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Invoices = lazy(() => import('./pages/Invoices'));
const MyTray = lazy(() => import('./pages/MyTray'));
const InvoiceDetails = lazy(() => import('./pages/InvoiceDetails'));
const InvoiceDetailViewPage = lazy(() => import('./pages/InvoiceDetailViewPage'));
const InvoiceFinancialApprovalPage = lazy(() => import('./pages/InvoiceFinancialApprovalPage'));
const Vendors = lazy(() => import('./pages/Vendors'));
const VendorCreatePage = lazy(() => import('./pages/VendorCreatePage'));
const VendorEditPage = lazy(() => import('./pages/VendorEditPage'));
const Vendor = lazy(() => import('./pages/Vendor'));
const VendorDetails = lazy(() => import('./pages/VendorDetails'));
const ImportVendor = lazy(() => import('./components/ImportVendor'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const UserCreatePage = lazy(() => import('./pages/UserCreatePage'));
const UserEditPage = lazy(() => import('./pages/UserEditPage'));
const Departments = lazy(() => import('./pages/Departments'));
const Reports = lazy(() => import('./pages/Reports'));
const ReportsInvoiceDetailPage = lazy(() => import('./pages/ReportsInvoiceDetailPage'));
const Currencies = lazy(() => import('./pages/Currencies'));
const DebitAccountExpenses = lazy(() => import('./pages/DebitAccountExpenses'));
const FinancialPowerProposal = lazy(() => import('./pages/FinancialPowerProposal'));
const FinancialPowerProposalDetails = lazy(() => import('./pages/FinancialPowerProposalDetails'));
const ApprovalMatrix = lazy(() => import('./pages/ApprovalMatrix'));
const Faq = lazy(() => import('./pages/Faq'));

// VMS pages
const VMSIndex = lazy(() => import('./pages/vms/VMSIndex'));
const VMSDashboard = lazy(() => import('./pages/vms/Dashboard'));
const PreOnboardingOverview = lazy(() => import('./pages/vms/PreOnboardingOverview'));
const PreOnboardingVendorInitiation = lazy(() => import('./pages/vms/PreOnboardingVendorInitiation'));
const PreOnboardingInProgress = lazy(() => import('./pages/vms/PreOnboardingInProgress'));
const PreOnboardingInProgressDetail = lazy(() => import('./pages/vms/PreOnboardingInProgressDetail'));
const PreOnboardingVendorInitiationView = lazy(() => import('./pages/vms/PreOnboardingVendorInitiationView'));
const PreOnboardingProspectiveVendorsPage = lazy(() => import('./pages/vms/PreOnboardingProspectiveVendorsPage'));
const PreOnboardingPvVpHubPage = lazy(() => import('./pages/vms/PreOnboardingPvVpHubPage'));
const PreOnboardingVendorEvaluationPage = lazy(() => import('./pages/vms/PreOnboardingVendorEvaluationPage'));
const PreOnboardingFinancialApprovalPage = lazy(() => import('./pages/vms/PreOnboardingFinancialApprovalPage'));
const VendorInitiation = lazy(() => import('./pages/vms/VendorInitiation'));
const VendorInitiationRequests = lazy(() => import('./pages/vms/VendorInitiationRequests'));
const PendingVendorInitiationAnnexures = lazy(() => import('./pages/vms/PendingVendorInitiationAnnexures'));
const VendorInitiationDetail = lazy(() => import('./pages/vms/VendorInitiationDetail'));
const FlowStakeholders = lazy(() => import('./pages/vms/FlowStakeholders'));
const FlowStakeholdersListPage = lazy(() => import('./pages/vms/FlowStakeholdersListPage'));
const FlowStakeholdersDetailPage = lazy(() => import('./pages/vms/FlowStakeholdersDetailPage'));
const CreateVendorInitiation = lazy(() => import('./pages/vms/CreateVendorInitiation'));
const VendorInitiationAnnexure7 = lazy(() => import('./pages/vms/VendorInitiationAnnexure7'));
const VendorInitiationFieldOptionsAdmin = lazy(() => import('./pages/vms/VendorInitiationFieldOptionsAdmin'));
const MaterialityAssessment = lazy(() => import('./pages/vms/MaterialityAssessment'));
const ProspectiveVendors = lazy(() => import('./pages/vms/ProspectiveVendors'));
const CreateProspectiveVendor = lazy(() => import('./pages/vms/CreateProspectiveVendor'));
const ProspectiveVendorDetail = lazy(() => import('./pages/vms/ProspectiveVendorDetail'));
const RFPManagement = lazy(() => import('./pages/vms/RFPManagement'));
const CreateRFP = lazy(() => import('./pages/vms/CreateRFP'));
const RFPDetail = lazy(() => import('./pages/vms/RFPDetail'));
const VendorProposals = lazy(() => import('./pages/vms/VendorProposals'));
const StakeholderVendorProposals = lazy(() => import('./pages/vms/StakeholderVendorProposals'));
const VendorProposalDetail = lazy(() => import('./pages/vms/VendorProposalDetail'));
const CreateVendorProposal = lazy(() => import('./pages/vms/CreateVendorProposal'));
const VendorEvaluations = lazy(() => import('./pages/vms/VendorEvaluations'));
const CreateVendorEvaluation = lazy(() => import('./pages/vms/CreateVendorEvaluation'));
const VendorEvaluationDetail = lazy(() => import('./pages/vms/VendorEvaluationDetail'));
const VendorEvaluationPendingStakeholder = lazy(
  () => import('./pages/vms/VendorEvaluationPendingStakeholder')
);
const VendorEvaluationPendingBusinessHead = lazy(
  () => import('./pages/vms/VendorEvaluationPendingBusinessHead')
);
const FinancialApproval = lazy(() => import('./pages/vms/FinancialApproval'));
const CreateFinancialApproval = lazy(() => import('./pages/vms/CreateFinancialApproval'));
const EditFinancialApproval = lazy(() => import('./pages/vms/EditFinancialApproval'));
const FinancialApprovalDetail = lazy(() => import('./pages/vms/FinancialApprovalDetail'));
const RegisteredVendors = lazy(() => import('./pages/vms/RegisteredVendors'));
const RegisteredVendorDetail = lazy(() => import('./pages/vms/RegisteredVendorDetail'));
const OnboardingAnnexureFilling = lazy(() => import('./pages/vms/OnboardingAnnexureFilling'));
const OnboardingContractFilling = lazy(() => import('./pages/vms/OnboardingContractFilling'));
const OnboardingContractHeadReview = lazy(() => import('./pages/vms/OnboardingContractHeadReview'));
const OnboardingContractManagementFormPage = lazy(
  () => import('./pages/vms/OnboardingContractManagementFormPage')
);
const OnboardingDpiaFilling = lazy(() => import('./pages/vms/OnboardingDpiaFilling'));
const OnboardingVendorHub = lazy(() => import('./pages/vms/OnboardingVendorHub'));
const OnboardingVendorPipeline = lazy(() => import('./pages/vms/OnboardingVendorPipeline'));
const OnboardingNotificationRegulatorPage = lazy(
  () => import('./pages/vms/OnboardingNotificationRegulatorPage'),
);
const OnboardingRegisterFillingPage = lazy(
  () => import('./pages/vms/OnboardingRegisterFillingPage'),
);
const Onboarding2Hub = lazy(() => import('./pages/vms/Onboarding2Hub'));
const Onboarding2Detail = lazy(() => import('./pages/vms/Onboarding2Detail'));
const Onboarding2NotificationRegulatorPage = lazy(
  () => import('./pages/vms/Onboarding2NotificationRegulatorPage'),
);
const Onboarding2AnnexuresFilling = lazy(() => import('./pages/vms/Onboarding2AnnexuresFilling'));
const Onboarding2AnnexureRedirect = lazy(() => import('./pages/vms/Onboarding2AnnexureRedirect'));
const OngoingVendorReviews = lazy(() => import('./pages/vms/OngoingVendorReviews'));
const ContractManagement = lazy(() => import('./pages/vms/ContractManagement'));
const ContractManagementVendorFormPage = lazy(
  () => import('./pages/vms/ContractManagementVendorFormPage'),
);
const ContractDefinitionEditor = lazy(() => import('./pages/vms/ContractDefinitionEditor'));
const ContractView = lazy(() => import('./pages/vms/ContractView'));
const CertificationsLicensesPage = lazy(() => import('./pages/vms/CertificationsLicensesPage'));
const CertificationsLicensesStaticPage = lazy(() => import('./pages/vms/CertificationsLicensesStaticPage'));
const Management2CertificationsDetailPage = lazy(() => import('./pages/vms/Management2CertificationsDetailPage'));
const AdminConfigRegisterPage = lazy(() => import('./pages/vms/adminConfig/AdminConfigRegisterPage'));
const AdminConfigRegisterDetailPage = lazy(
  () => import('./pages/vms/adminConfig/AdminConfigRegisterDetailPage'),
);
const AdminConfigRegisterEditPage = lazy(
  () => import('./pages/vms/adminConfig/AdminConfigRegisterEditPage'),
);
const AdminConfigRegisterAmendPage = lazy(
  () => import('./pages/vms/adminConfig/AdminConfigRegisterAmendPage'),
);
const AdminConfigRegisterHistoryPage = lazy(
  () => import('./pages/vms/adminConfig/AdminConfigRegisterHistoryPage'),
);
const AdminConfigDepartmentCreationPage = lazy(
  () => import('./pages/vms/adminConfig/AdminConfigDepartmentCreationPage'),
);
const AdminConfigUserRoleManagementPage = lazy(
  () => import('./pages/vms/adminConfig/AdminConfigUserRoleManagementPage'),
);
const AdminConfigMasterDataPage = lazy(() => import('./pages/vms/adminConfig/AdminConfigMasterDataPage'));
const VendorExit = lazy(() => import('./pages/vms/VendorExit'));
const VendorExitDetail = lazy(() => import('./pages/vms/VendorExitDetail'));
const AnnexureManagement = lazy(() => import('./pages/vms/AnnexureManagement'));
const AnnexureDefinitionEditor = lazy(() => import('./pages/vms/AnnexureDefinitionEditor'));
const AnnexureView = lazy(() => import('./pages/vms/AnnexureView'));
const DpiaManagement = lazy(() => import('./pages/vms/DpiaManagement'));
const DpiaDefinitionEditor = lazy(() => import('./pages/vms/DpiaDefinitionEditor'));
const DpiaView = lazy(() => import('./pages/vms/DpiaView'));
const AuditLogs = lazy(() => import('./pages/vms/AuditLogs'));
const VmsMyTray = lazy(() => import('./pages/vms/VmsMyTray'));

// Route-level error boundary wrapper component with Suspense for lazy loading
const RouteErrorBoundary = ({ children }: { children: React.ReactNode }) => (
  <ErrorBoundary>
    <Suspense fallback={<PageLoader message="Loading..." />}>{children}</Suspense>
  </ErrorBoundary>
);

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SocketProvider>
          <div className=" bg-gray-50">
          {/* Skip to main content link - visible on focus for keyboard users */}
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            Skip to main content
          </a>
          <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false} />
          <BrandDocumentHead />
          <Routes>
            {/* Public routes */}
            <Route
              path="/login"
              element={
                <RouteErrorBoundary>
                  <Login />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/forget-password/*"
              element={
                <RouteErrorBoundary>
                  <ResetPassword />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <RouteErrorBoundary>
                  <ForgotPassword />
                </RouteErrorBoundary>
              }
            />

            {/* System Selection - Protected */}
            <Route
              path="/system-selection"
              element={
                <ProtectedRoute>
                  <RouteErrorBoundary>
                    <SystemSelection />
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />

            {/* IMS Routes - Protected */}
            <Route
              path="/ims/*"
              element={
                <ProtectedRoute>
                  <ImsPlatformGuard>
                    <Layout />
                  </ImsPlatformGuard>
                </ProtectedRoute>
              }
            >
              <Route index element={<DefaultAppRedirect />} />
              <Route
                path="dashboard"
                element={
                  <RouteErrorBoundary>
                    <AdminRoute>
                      <Dashboard />
                    </AdminRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="invoices"
                element={
                  <RouteErrorBoundary>
                    <Invoices />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="my-tray"
                element={
                  <RouteErrorBoundary>
                    <MyTray />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="invoices/:id/detail"
                element={
                  <RouteErrorBoundary>
                    <InvoiceDetailViewPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="financial-approval/:id/detail"
                element={
                  <RouteErrorBoundary>
                    <InvoiceFinancialApprovalPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="invoices/:id"
                element={
                  <RouteErrorBoundary>
                    <InvoiceDetails />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor"
                element={
                  <RouteErrorBoundary>
                    <Vendor />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor/:id"
                element={
                  <RouteErrorBoundary>
                    <VendorDetails />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendors"
                element={
                  <RouteErrorBoundary>
                    <Vendors />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendors/create"
                element={
                  <RouteErrorBoundary>
                    <VendorCreatePage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendors/:id/edit"
                element={
                  <RouteErrorBoundary>
                    <VendorEditPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendors/import"
                element={
                  <RouteErrorBoundary>
                    <ImportVendor />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="departments"
                element={
                  <RouteErrorBoundary>
                    <Departments />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="currencies"
                element={
                  <RouteErrorBoundary>
                    <Currencies />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="debit-accounts"
                element={
                  <RouteErrorBoundary>
                    <DebitAccountExpenses />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="financial-power-proposal"
                element={
                  <RouteErrorBoundary>
                    <FinancialPowerProposal />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="financial-power-proposal/:id"
                element={
                  <RouteErrorBoundary>
                    <FinancialPowerProposalDetails />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="user-management"
                element={
                  <RouteErrorBoundary>
                    <UserManagement />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="user-management/create"
                element={
                  <RouteErrorBoundary>
                    <UserCreatePage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="user-management/edit/:id"
                element={
                  <RouteErrorBoundary>
                    <UserEditPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="reports/invoices/:id/detail"
                element={
                  <RouteErrorBoundary>
                    <ReportsInvoiceDetailPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="reports"
                element={
                  <RouteErrorBoundary>
                    <Reports />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="approval-matrix"
                element={
                  <RouteErrorBoundary>
                    <ApprovalMatrix />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="faq"
                element={
                  <RouteErrorBoundary>
                    <Faq />
                  </RouteErrorBoundary>
                }
              />
            </Route>

            {/* VMS Routes - Protected */}
            <Route
              path="/vms/*"
              element={
                <ProtectedRoute>
                  <VmsPlatformGuard>
                    <AnnexureFillingNavProvider>
                      <VMSLayout />
                    </AnnexureFillingNavProvider>
                  </VmsPlatformGuard>
                </ProtectedRoute>
              }
            >
              <Route index element={<RouteErrorBoundary><VMSIndex /></RouteErrorBoundary>} />
              <Route
                path="dashboard"
                element={
                  <RouteErrorBoundary>
                    <VMSDashboard />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="pre-onboarding/overview"
                element={
                  <RouteErrorBoundary>
                    <PreOnboardingOverview />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="pre-onboarding/vendor-initiation"
                element={
                  <RouteErrorBoundary>
                    <PreOnboardingVendorInitiation />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="pre-onboarding/in-progress/:journeyId/vendor-initiation"
                element={
                  <RouteErrorBoundary>
                    <PreOnboardingVendorInitiationView />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="pre-onboarding/in-progress/:journeyId/prospective-vendors-proposal"
                element={
                  <RouteErrorBoundary>
                    <PreOnboardingPvVpHubPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="pre-onboarding/in-progress/:journeyId/prospective-vendors"
                element={
                  <RouteErrorBoundary>
                    <PreOnboardingProspectiveVendorsPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="pre-onboarding/in-progress/:journeyId/vendor-evaluation"
                element={
                  <RouteErrorBoundary>
                    <PreOnboardingVendorEvaluationPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="pre-onboarding/in-progress/:journeyId/financial-approval"
                element={
                  <RouteErrorBoundary>
                    <PreOnboardingFinancialApprovalPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="pre-onboarding/in-progress/:id"
                element={
                  <RouteErrorBoundary>
                    <PreOnboardingInProgressDetail />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="pre-onboarding/in-progress"
                element={
                  <RouteErrorBoundary>
                    <PreOnboardingInProgress />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="my-tray"
                element={
                  <RouteErrorBoundary>
                    <VmsMyTray />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor-initiation"
                element={
                  <RouteErrorBoundary>
                    <VendorInitiation />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor-initiation-requests"
                element={
                  <RouteErrorBoundary>
                    <VendorInitiationRequests />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor-initiation/annexures/pending"
                element={
                  <RouteErrorBoundary>
                    <PendingVendorInitiationAnnexures />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor-initiation/create"
                element={
                  <RouteErrorBoundary>
                    <CreateVendorInitiation />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor-initiation/annexure-7/:initiationId"
                element={
                  <RouteErrorBoundary>
                    <VendorInitiationAnnexure7 />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor-initiation/annexure-7"
                element={
                  <RouteErrorBoundary>
                    <VendorInitiationAnnexure7 />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor-initiation/:id/edit"
                element={
                  <RouteErrorBoundary>
                    <CreateVendorInitiation />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor-initiation/:id"
                element={
                  <RouteErrorBoundary>
                    <VendorInitiationDetail />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="flow-stakeholders/legacy"
                element={
                  <RouteErrorBoundary>
                    <VmsBusinessOwnerRoute>
                      <FlowStakeholders />
                    </VmsBusinessOwnerRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="flow-stakeholders/:initiationId"
                element={
                  <RouteErrorBoundary>
                    <VmsBusinessOwnerRoute>
                      <FlowStakeholdersDetailPage />
                    </VmsBusinessOwnerRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="flow-stakeholders"
                element={
                  <RouteErrorBoundary>
                    <VmsBusinessOwnerRoute>
                      <FlowStakeholdersListPage />
                    </VmsBusinessOwnerRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="user-management"
                element={
                  <RouteErrorBoundary>
                    <VmsAdminRoute>
                      <UserManagement />
                    </VmsAdminRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="user-management/create"
                element={
                  <RouteErrorBoundary>
                    <VmsAdminRoute>
                      <UserCreatePage />
                    </VmsAdminRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="user-management/edit/:id"
                element={
                  <RouteErrorBoundary>
                    <VmsAdminRoute>
                      <UserEditPage />
                    </VmsAdminRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor-initiation-field-options"
                element={
                  <RouteErrorBoundary>
                    <VmsAdminRoute>
                      <VendorInitiationFieldOptionsAdmin />
                    </VmsAdminRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="admin-config/register/:rowId/history"
                element={
                  <RouteErrorBoundary>
                    <AdminConfigRegisterHistoryPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="admin-config/register/:rowId/edit"
                element={
                  <RouteErrorBoundary>
                    <VmsAdminRoute>
                      <AdminConfigRegisterEditPage />
                    </VmsAdminRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="admin-config/register/:rowId/amend"
                element={
                  <RouteErrorBoundary>
                    <AdminConfigRegisterAmendPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="admin-config/register/:rowId"
                element={
                  <RouteErrorBoundary>
                    <AdminConfigRegisterDetailPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="admin-config/register"
                element={
                  <RouteErrorBoundary>
                    <AdminConfigRegisterPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="admin-config/annexure-management"
                element={
                  <RouteErrorBoundary>
                    <VmsAdminRoute>
                      <AnnexureManagement />
                    </VmsAdminRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="admin-config/department-creation"
                element={
                  <RouteErrorBoundary>
                    <VmsAdminRoute>
                      <AdminConfigDepartmentCreationPage />
                    </VmsAdminRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="admin-config/user-role-management"
                element={
                  <RouteErrorBoundary>
                    <VmsAdminRoute>
                      <AdminConfigUserRoleManagementPage />
                    </VmsAdminRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="admin-config/master-data"
                element={
                  <RouteErrorBoundary>
                    <VmsAdminRoute>
                      <AdminConfigMasterDataPage />
                    </VmsAdminRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="materiality-assessment"
                element={
                  <RouteErrorBoundary>
                    <MaterialityAssessment />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="prospective-vendors/create/:draftId"
                element={
                  <RouteErrorBoundary>
                    <CreateProspectiveVendor />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="prospective-vendors/create"
                element={
                  <RouteErrorBoundary>
                    <CreateProspectiveVendor />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="prospective-vendors/:id"
                element={
                  <RouteErrorBoundary>
                    <ProspectiveVendorDetail />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="prospective-vendors"
                element={
                  <RouteErrorBoundary>
                    <ProspectiveVendors />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor-proposals/create"
                element={
                  <RouteErrorBoundary>
                    <CreateVendorProposal />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor-proposals/:id/edit"
                element={
                  <RouteErrorBoundary>
                    <CreateVendorProposal />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor-proposals/stakeholder"
                element={
                  <RouteErrorBoundary>
                    <StakeholderVendorProposals />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor-proposals/:id"
                element={
                  <RouteErrorBoundary>
                    <VendorProposalDetail />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor-proposals"
                element={
                  <RouteErrorBoundary>
                    <VendorProposals />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor-evaluations/create"
                element={
                  <RouteErrorBoundary>
                    <CreateVendorEvaluation />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor-evaluations/edit/:evaluationId"
                element={
                  <RouteErrorBoundary>
                    <CreateVendorEvaluation />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor-evaluations/pending/stakeholder"
                element={
                  <RouteErrorBoundary>
                    <VendorEvaluationPendingStakeholder />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor-evaluations/pending/business-head"
                element={
                  <RouteErrorBoundary>
                    <VendorEvaluationPendingBusinessHead />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor-evaluations/:id"
                element={
                  <RouteErrorBoundary>
                    <VendorEvaluationDetail />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor-evaluations"
                element={
                  <RouteErrorBoundary>
                    <VendorEvaluations />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="financial-approval/create"
                element={
                  <RouteErrorBoundary>
                    <CreateFinancialApproval />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="financial-approval/:id/edit"
                element={
                  <RouteErrorBoundary>
                    <EditFinancialApproval />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="financial-approval/:id"
                element={
                  <RouteErrorBoundary>
                    <FinancialApprovalDetail />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="financial-approval"
                element={
                  <RouteErrorBoundary>
                    <FinancialApproval />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="registered-vendors/:id"
                element={
                  <RouteErrorBoundary>
                    <RegisteredVendorDetail />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="registered-vendors"
                element={
                  <RouteErrorBoundary>
                    <RegisteredVendors />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="rfp-management"
                element={
                  <RouteErrorBoundary>
                    <RFPManagement />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="rfp-management/create/:draftId"
                element={
                  <RouteErrorBoundary>
                    <CreateRFP />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="rfp-management/create"
                element={
                  <RouteErrorBoundary>
                    <CreateRFP />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="rfp-management/:id"
                element={
                  <RouteErrorBoundary>
                    <RFPDetail />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="onboarding/vendors/:registeredVendorId/annexures"
                element={
                  <RouteErrorBoundary>
                    <OnboardingVendorAnnexuresRedirect />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="onboarding/vendors/:registeredVendorId/notification-regulator"
                element={
                  <RouteErrorBoundary>
                    <OnboardingNotificationRegulatorPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="onboarding/vendors/:registeredVendorId/register-filling"
                element={
                  <RouteErrorBoundary>
                    <OnboardingRegisterFillingPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="onboarding/vendors/:registeredVendorId"
                element={
                  <RouteErrorBoundary>
                    <OnboardingVendorPipeline />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="onboarding"
                element={
                  <RouteErrorBoundary>
                    <OnboardingVendorHub />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="onboarding/vendor-additional-data"
                element={<Navigate to="/vms/onboarding/annexures" replace />}
              />
              <Route
                path="onboarding/annexures"
                element={
                  <RouteErrorBoundary>
                    <OnboardingAnnexureFilling />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="onboarding/contracts/review/workspace"
                element={
                  <RouteErrorBoundary>
                    <OnboardingContractHeadReview />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="onboarding/contracts/review/edit"
                element={
                  <RouteErrorBoundary>
                    <OnboardingContractManagementFormPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="onboarding/contracts/review/view"
                element={
                  <RouteErrorBoundary>
                    <OnboardingContractManagementFormPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="onboarding/contracts/review"
                element={
                  <RouteErrorBoundary>
                    <OnboardingContractHeadReview />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="onboarding/contracts"
                element={
                  <RouteErrorBoundary>
                    <OnboardingContractFilling />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="onboarding/dpia"
                element={
                  <RouteErrorBoundary>
                    <OnboardingDpiaFilling />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="onboarding2/:serviceId/annexures/:annexureId"
                element={
                  <RouteErrorBoundary>
                    <Onboarding2AnnexureRedirect />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="onboarding2/:serviceId/annexures"
                element={
                  <RouteErrorBoundary>
                    <Onboarding2AnnexuresFilling />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="onboarding2/:serviceId/notification-regulator"
                element={
                  <RouteErrorBoundary>
                    <Onboarding2NotificationRegulatorPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="onboarding2/:serviceId"
                element={
                  <RouteErrorBoundary>
                    <Onboarding2Detail />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="onboarding2"
                element={
                  <RouteErrorBoundary>
                    <Onboarding2Hub />
                  </RouteErrorBoundary>
                }
              />
              <Route path="ongoing2" element={<OngoingVendorReviewsLegacyTabRedirect />} />
              <Route
                path="ongoing"
                element={
                  <RouteErrorBoundary>
                    <OngoingVendorReviews />
                  </RouteErrorBoundary>
                }
              />
              <Route path="vendor-onboarding" element={<Navigate to="/vms/onboarding" replace />} />
              <Route
                path="contract-management/edit/:code"
                element={
                  <RouteErrorBoundary>
                    <VmsLegalOrAdminRoute>
                      <ContractDefinitionEditor />
                    </VmsLegalOrAdminRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="contract-management/forms/view/:code"
                element={
                  <RouteErrorBoundary>
                    <VmsLegalOrAdminRoute>
                      <ContractView />
                    </VmsLegalOrAdminRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="contract-management/view/:registeredVendorId"
                element={
                  <RouteErrorBoundary>
                    <VmsLegalOrAdminRoute>
                      <ContractManagementVendorFormPage />
                    </VmsLegalOrAdminRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="contract-management"
                element={
                  <RouteErrorBoundary>
                    <VmsLegalOrAdminRoute>
                      <ContractManagement />
                    </VmsLegalOrAdminRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route path="outsourcing-register" element={<Navigate to="/vms/admin-config/register" replace />} />
              <Route
                path="management2/certifications-licenses/:rowId"
                element={
                  <RouteErrorBoundary>
                    <Management2CertificationsDetailPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="management2/certifications-licenses"
                element={
                  <RouteErrorBoundary>
                    <CertificationsLicensesStaticPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="management2/flow-stakeholders/:initiationId"
                element={
                  <RouteErrorBoundary>
                    <VmsBusinessOwnerRoute>
                      <FlowStakeholdersDetailPage />
                    </VmsBusinessOwnerRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="management2/flow-stakeholders"
                element={
                  <RouteErrorBoundary>
                    <VmsBusinessOwnerRoute>
                      <FlowStakeholdersListPage />
                    </VmsBusinessOwnerRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="certifications/legacy"
                element={
                  <RouteErrorBoundary>
                    <CertificationsLicensesPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="certifications/:rowId"
                element={
                  <RouteErrorBoundary>
                    <Management2CertificationsDetailPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="certifications"
                element={
                  <RouteErrorBoundary>
                    <CertificationsLicensesStaticPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="outsourcing-vendors/:id"
                element={
                  <RouteErrorBoundary>
                    <RedirectLegacyOutsourcingVendorDetail />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="outsourcing-vendors"
                element={
                  <RouteErrorBoundary>
                    <Navigate to="/vms/admin-config/register" replace />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="ongoing/annexures"
                element={<OngoingAnnexuresLegacyRedirect />}
              />
              <Route path="ongoing/annexures/:assessmentType" element={<OngoingAnnexuresSegmentLegacyRedirect />} />
              <Route path="ongoing-management" element={<Navigate to="/vms/ongoing" replace />} />
              <Route
                path="vendor-exit/:outsourcingVendorId"
                element={
                  <RouteErrorBoundary>
                    <VendorExitDetail />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="vendor-exit"
                element={
                  <RouteErrorBoundary>
                    <VendorExit />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="annexure-management/edit/:code"
                element={
                  <RouteErrorBoundary>
                    <VmsAnnexureManagementRoute>
                      <AnnexureDefinitionEditor />
                    </VmsAnnexureManagementRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="annexure-management/view/:code"
                element={
                  <RouteErrorBoundary>
                    <VmsAnnexureManagementRoute>
                      <AnnexureView />
                    </VmsAnnexureManagementRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="annexure-management"
                element={
                  <RouteErrorBoundary>
                    <VmsAnnexureManagementRoute>
                      <AnnexureManagement />
                    </VmsAnnexureManagementRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="dpia-management/edit/:code"
                element={
                  <RouteErrorBoundary>
                    <VmsDpiaManagementRoute>
                      <DpiaDefinitionEditor />
                    </VmsDpiaManagementRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="dpia-management/view/:code"
                element={
                  <RouteErrorBoundary>
                    <VmsDpiaManagementRoute>
                      <DpiaView />
                    </VmsDpiaManagementRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="dpia-management"
                element={
                  <RouteErrorBoundary>
                    <VmsDpiaManagementRoute>
                      <DpiaManagement />
                    </VmsDpiaManagementRoute>
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="audit-logs"
                element={
                  <RouteErrorBoundary>
                    <VmsAdminRoute>
                      <AuditLogs />
                    </VmsAdminRoute>
                  </RouteErrorBoundary>
                }
              />
            </Route>

            {/* Catch all route */}
            <Route path="*" element={<AuthenticatedCatchAllRedirect />} />
          </Routes>
        </div>
        </SocketProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
